import type { BackPageJson, FrontColorsBackup } from '@apps/card/routes/CardCustomizer/DraftJson.types';
import type { PageJson } from '@apps/card/routes/CardCustomizer/components/PageJson.types';
import type { LayerData } from '@apps/card/routes/CardCustomizer/components/Layer.types';
import { cloneDeep } from 'lodash-es';
import type { CardColorRoles } from './colorway';
import { resolveColorRoles } from './colorway';

/**
 * "Match front colors": paints the card front's fill and ink onto the back.
 *
 * The back is a separate template (`side: 'back'`) with its own literal colors and no colorways of
 * its own, so the two colors are read off the front with `resolveColorRoles` and written straight
 * into the back's `fill` and ink fields. Concrete values rather than `var(--N)` tokens, because
 * every surface that draws a back — editor, proof PDF, review, digital ecard, envelope reveal —
 * already knows how to draw literal colors, and several of them never resolve colorway tokens.
 *
 * The back's own colors are kept in `frontColorsBackup` so the toggle can be turned off again.
 * Nothing here is needed to *render* a back; the flag and the backup are editor bookkeeping.
 */

/**
 * Back layouts whose design is the customer's own uploaded artwork. There is nothing of ours to
 * recolor, so the toggle is hidden rather than left to no-op. The `upload_your_own*` ids are the
 * deprecated spellings, still present on older drafts (see `CardBackChoiceGroup`).
 */
const UPLOAD_YOUR_DESIGN_LAYOUTS: ReadonlySet<string> = new Set(['upload_your_design', 'upload_your_design_7x5', 'upload_your_own', 'upload_your_own_design']);

/**
 * A layer's ink color, or `undefined` for the layer types that carry none.
 *
 * Rectangles are deliberately excluded: on the 19 back templates that have them they are accent
 * bars and dividers, not body copy, and recoloring them was scoped out. Images and foil have no
 * color field to begin with.
 */
function readInk(layer: LayerData): string | undefined {
  if (layer.type === 'text') return layer.textData.fill;
  if (layer.type === 'qrcode') return layer.qrcodeData.fill;
  return undefined;
}

function writeInk(layer: LayerData, color: string): void {
  if (layer.type === 'text') layer.textData.fill = color;
  else if (layer.type === 'qrcode') layer.qrcodeData.fill = color;
}

/**
 * Whether the toggle should be offered at all: the front has to have colorways to borrow from, and
 * the back has to be one we own the design of.
 *
 * Note this asks whether the *front* carries colorways, not whether recoloring would visibly change
 * this particular back. A back whose photo covers its own fill still recolors its text, and a photo
 * back with no text is a harmless no-op — neither is worth a different-looking panel.
 */
export function canUseFrontColors(front: PageJson | undefined, back: (BackPageJson & { layout: string }) | undefined): boolean {
  if (!front?.colorways?.options?.length || !back) return false;
  return isRecolorableBackLayout(back.layout);
}

/** Whether a back layout is one we own the design of, and can therefore recolor. */
export function isRecolorableBackLayout(layout: string): boolean {
  return !UPLOAD_YOUR_DESIGN_LAYOUTS.has(layout);
}

/**
 * Paints a resolved pair of roles onto a page. Colors only — no draft bookkeeping.
 *
 * Ink covers text, QR codes and any image in `inkGlyphSrcs`. A glyph carries its color in its
 * pixels rather than a field, so it gets `imageData.fill` and the renderers mask it to that color
 * through its own alpha. Which images qualify cannot be read from the layer — it is measured from
 * the image itself, by `findInkGlyphSrcs`. Omit the set and no image is touched.
 */
function paintColorRoles(page: PageJson, roles: CardColorRoles, inkGlyphSrcs?: ReadonlySet<string>): void {
  if (roles.fill !== undefined) page.fill = roles.fill;
  const ink = roles.text;
  if (ink === undefined) return;
  page.layers.forEach(layer => {
    writeInk(layer, ink);
    if (layer.type === 'image' && inkGlyphSrcs?.has(layer.imageData.src)) layer.imageData.fill = ink;
  });
}

/**
 * A display-only copy of `page` wearing the front's colors.
 *
 * For previews that must show what picking a layout *would* look like while the toggle is on — the
 * back layout thumbnails. Deliberately returns a clone and writes no `useFrontColors` /
 * `frontColorsBackup`: the originals are what `setTemplate` copies into the draft, and recoloring
 * them in place would make the draft's backup snapshot the front's colors instead of the back's.
 */
export function previewWithFrontColors(page: PageJson, front: PageJson, inkGlyphSrcs?: ReadonlySet<string>): PageJson {
  const preview = cloneDeep(page);
  paintColorRoles(preview, resolveColorRoles(front), inkGlyphSrcs);
  return preview;
}

/** Snapshots the colors currently on the back, aligned to `layers` so a restore can retrace them. */
function captureBackup(back: BackPageJson): FrontColorsBackup {
  return { fill: back.fill, inks: back.layers.map(layer => readInk(layer) ?? null) };
}

/**
 * Writes the front's colors onto the back, capturing what was there first.
 *
 * Immer-mutating, to match the `update(draft => ...)` callers.
 *
 * Idempotent: the backup is captured only when there is nothing to restore yet. Applying twice
 * would otherwise snapshot the colors this function just wrote, stranding the back's own colors
 * with no way back — the toggle would then flip on and off with no visible effect. A caller that
 * genuinely needs a fresh capture, because the layers underneath have been replaced, drops the old
 * backup first (see `syncFrontColorsOnBack`).
 *
 * Roles the front cannot supply are left alone rather than guessed at: a front with no text layers
 * recolors the back's fill only.
 */
export function applyFrontColorsToBack(front: PageJson, back: BackPageJson, inkGlyphSrcs?: ReadonlySet<string>): void {
  const { fill, text } = resolveColorRoles(front);
  if (!back.frontColorsBackup) back.frontColorsBackup = captureBackup(back);
  back.useFrontColors = true;
  paintColorRoles(back, { fill, text }, inkGlyphSrcs);
}

/**
 * Puts the back's own colors back and turns the flag off.
 *
 * Restores by position, skipping layers that no longer carry ink — the backup is re-captured on
 * every layout change, so the two can only fall out of step if a layer is added or removed while
 * the toggle is on, which nothing in the editor does today.
 */
export function clearFrontColorsFromBack(back: BackPageJson): void {
  const backup = back.frontColorsBackup;
  if (backup) {
    if (backup.fill !== undefined) back.fill = backup.fill;
    back.layers.forEach((layer, index) => {
      const ink = backup.inks[index];
      if (ink !== null && ink !== undefined) writeInk(layer, ink);
    });
  }
  // Templates never author a fill, so dropping it restores the glyph's own color. Done outside the
  // backup branch so a draft that somehow lost its backup still sheds the recoloring.
  back.layers.forEach(layer => {
    if (layer.type === 'image') delete layer.imageData.fill;
  });
  delete back.frontColorsBackup;
  delete back.useFrontColors;
}

/**
 * Re-applies the front's colors after the back layout changes, so a ticked toggle survives picking
 * a different back. A no-op when the toggle is off, which is the default.
 *
 * Call it from `setTemplate`, after the new template's layers have been written onto the draft.
 */
export function syncFrontColorsOnBack(front: PageJson, back: BackPageJson & { layout: string }, inkGlyphSrcs?: ReadonlySet<string>): void {
  if (!back.useFrontColors) return;
  // `layers` are the new layout's, still in its own colors, and the backup describes the layers
  // that were just replaced. Drop it rather than restore it onto unrelated layers.
  delete back.frontColorsBackup;
  if (!canUseFrontColors(front, back)) {
    delete back.useFrontColors;
    return;
  }
  applyFrontColorsToBack(front, back, inkGlyphSrcs);
}
